/**
 * /es/registro-de-decisiones — decision-log concept page (Spanish). Wedge.
 * Links to /es/ia-para-tomar-notas-de-reuniones bridge.
 */
import { MarketingShell } from './MarketingShell';

export function EsRegistroDecisiones() {
  return (
    <MarketingShell
      currentLocale="es"
      hreflangPath="/registro-de-decisiones"
      hreflangLocales={['es']}
      documentTitle="Registro de decisiones (decision log): por qué y cómo · Draftlize"
      documentDescription="Un registro de decisiones guarda el «porqué» de cada elección de producto. La mayoría se pudre: se escribe una vez y nadie lo relee. Cómo hacer uno vivo, con Draftlize."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guía · Registro de decisiones</span>
          <h1 className="serif" style={{ fontSize: 'clamp(33px, 4.4vw, 54px)', lineHeight: 1.07, margin: '12px 0 24px' }}>
            El registro de decisiones que responde<br />
            <em>«¿por qué hicimos esto?»</em>
          </h1>
          <p className="lede">
            Un registro de decisiones (decision log) es la bitácora que guarda, para cada elección de producto, <em>por qué</em> se decidió así. Meses después, cuando alguien reabre el mismo debate, tener a mano el contexto, las opciones y los compromisos lo zanja en un minuto. El problema: la mayoría de los registros se <em>pudren</em> — se escriben una vez, nadie los relee y quedan obsoletos en silencio. Aquí, cómo hacerlo y cómo mantenerlo vivo.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Qué registrar en un registro de decisiones.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>La decisión</strong> — qué se decidió, en una frase.</li>
            <li><strong>El contexto</strong> — por qué hacía falta decidir ahora.</li>
            <li><strong>Opciones y compromisos</strong> — qué se valoró y por qué se descartó el resto. <em>La parte más útil después</em>.</li>
            <li><strong>Responsable y fecha</strong> — quién y cuándo.</li>
            <li><strong>Alcance</strong> — a qué specs o decisiones afecta.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Este formato coincide con el ADR (registro de decisiones de arquitectura). Muchas decisiones nacen en reuniones — extraerlas primero con <a href="/es/ia-para-tomar-notas-de-reuniones" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>IA para notas de reuniones</a> es el punto de partida.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Por qué se pudren los registros. <em>Y cómo evitarlo.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Un registro de decisiones en texto plano empieza a desviarse de la realidad en cuanto lo escribes. Una decisión posterior revierte una anterior y el documento sigue mostrando la vieja. En Draftlize, las decisiones son «tarjetas vivas».
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Cada decisión es una tarjeta localizable</h3>
            <p>Una decisión = una tarjeta, con su contexto, opciones, motivo y responsable, referenciable por ID. En vez de perderse en un documento, siempre se puede señalar.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">La decisión revertida se marca «stale»</h3>
            <p>Cuando una decisión nueva revierte una anterior, la antigua enlazada — y las specs sobre ella — se marcan «stale» automáticamente. No se vuelve a debatir lo ya acordado.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Tu agente de IA lo lee y escribe</h3>
            <p>El registro se vuelve contexto que Claude Code o Cursor leen vía MCP. El agente escribe lo siguiente sabiendo «por qué» se decidió así.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          La mayoría de los registros de decisiones no los lee nadie al día siguiente. Por eso, seis meses después, el mismo debate empieza de cero.
        </blockquote>
        <cite>Guarda cada decisión con su contexto y señala lo que afecta cuando cambia. Eso es Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Preguntas frecuentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '¿Registro de decisiones o acta de reunión?', a: 'El acta es «el registro de la reunión»; el registro de decisiones es «la bitácora de las decisiones». Extraer solo las decisiones del acta y acumularlas es lo que de verdad sirve después.' },
            { q: '¿Quién debe mantenerlo?', a: 'Normalmente quien es responsable de la decisión (PM o lead). Lo clave es mantener rastreable el «porqué» de cada decisión.' },
            { q: '¿Cuánto detalle?', a: 'Basta con que se entiendan la decisión, el contexto y los compromisos. Más que la longitud, importa poder detectar cuándo una decisión queda obsoleta.' },
            { q: '¿Es lo mismo que un ADR?', a: 'El ADR es un tipo de registro de decisiones técnico. Draftlize trata tanto los ADR como las decisiones de producto como la misma «tarjeta viva».' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Un registro de decisiones que no se pudre <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Regístrate y recibe $5</span>
          <span>Paga solo por lo que usas</span>
          <span>El saldo nunca caduca</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Convierte cada decisión en una tarjeta con su contexto; si se revierte, se marca «stale» sola, y tu agente de IA la lee. Un registro de decisiones vivo, con Draftlize.
          </p>
          <a className="btn-primary" href="/">Empieza gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
